// cfoCashEvidence — decodes the evidence the cash model already ships.
//
// Every planning row carries an `evidence_json` array that the server passes
// through untouched from `ml_cash.rs`. It is rich and the drill has never read
// it: document rows carry days past due, the customer's own DSO, the
// collectibility probability and its basis; cadence rows carry the GL account,
// the learned pay-day and how many times it has been observed; recurring rows
// carry the stream's cadence and median amount. Surfacing it is what turns
// "Daily Forecast ML allocation" into "they pay in 47 days on average".
//
// Read-only: nothing here changes an amount.

import type { QuickBooksCashPlanningRow } from '../../../data/ServerApiClient'
import { trimStr } from './cfoFormat'

export type CashProvenance = 'actual' | 'document' | 'recurring' | 'modeled' | 'user'

const CADENCE_LABELS: Record<string, string> = {
  weekly: 'every week',
  biweekly: 'every other week',
  semimonthly: 'twice a month',
  monthly: 'every month',
}

const ORDINAL_SUFFIX = (day: number): string => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th'
  return ['th', 'st', 'nd', 'rd'][day % 10] ?? 'th'
}

/** The evidence array is a list of objects; the first hit for a key wins. */
const pick = (row: QuickBooksCashPlanningRow, key: string): unknown => {
  const evidence = row.evidence_json
  if (!Array.isArray(evidence)) return undefined
  for (const item of evidence) {
    if (item && typeof item === 'object' && key in item) {
      const value = (item as Record<string, unknown>)[key]
      if (value !== null && value !== undefined && value !== '') return value
    }
  }
  return undefined
}

const num = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const str = (value: unknown): string | null => {
  const text = trimStr(typeof value === 'string' ? value : value == null ? '' : String(value))
  return text || null
}

/**
 * Provenance, preferring the server's computed field and falling back to the
 * same derivation for payloads served by an older build.
 */
export const cashRowProvenance = (row: QuickBooksCashPlanningRow): CashProvenance => {
  const served = trimStr(row.provenance || '').toLowerCase()
  if (
    served === 'actual' ||
    served === 'document' ||
    served === 'recurring' ||
    served === 'modeled' ||
    served === 'user'
  ) {
    return served
  }
  if (row.row_kind === 'expectation') return 'user'
  if (row.row_kind === 'posted_actual') return 'actual'
  if (row.row_kind === 'bridge') return 'modeled'
  const sourceType = trimStr(row.source_type || '').toLowerCase()
  if (['invoice', 'ar_invoice', 'bill', 'ap_bill'].includes(sourceType)) return 'document'
  if (pick(row, 'stream_key')) return 'recurring'
  return 'modeled'
}

/** Human-readable one-liners describing WHY this row is in the plan. */
export const describeCashRow = (row: QuickBooksCashPlanningRow): string[] => {
  const chips: string[] = []
  const provenance = cashRowProvenance(row)

  if (provenance === 'document') {
    const daysPastDue = num(pick(row, 'days_past_due'))
    if (daysPastDue != null && daysPastDue > 0) {
      chips.push(`${Math.round(daysPastDue)} days past due`)
    } else if (daysPastDue != null) {
      chips.push('not yet due')
    }
    const dso = num(pick(row, 'dso_days'))
    if (dso != null && dso > 0) chips.push(`pays in ${Math.round(dso)} days on average`)
    const collectibility = num(pick(row, 'collectibility_probability'))
    if (collectibility != null && collectibility > 0 && collectibility < 1) {
      chips.push(`${Math.round(collectibility * 100)}% likely to collect`)
    }
    const basis = str(pick(row, 'collectibility_basis'))
    if (basis === 'vendor_due_date') chips.push('timed from the bill due date')
    if (basis === 'vendor_payment_behavior') chips.push('timed from how you pay this vendor')
    return chips
  }

  if (provenance === 'recurring') {
    const cadence = str(pick(row, 'stream_cadence'))?.toLowerCase()
    if (cadence && CADENCE_LABELS[cadence]) chips.push(CADENCE_LABELS[cadence])
    const observed = num(pick(row, 'observed_count'))
    if (observed != null && observed > 0) chips.push(`${Math.round(observed)} observed`)
    const median = num(pick(row, 'median_amount'))
    if (median != null && median !== 0) {
      chips.push(`usually ${Math.abs(median).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}`)
    }
    return chips
  }

  if (provenance === 'modeled') {
    const day = num(pick(row, 'learned_day_of_month'))
    if (day != null && day >= 1 && day <= 31) {
      const rounded = Math.round(day)
      chips.push(`usually the ${rounded}${ORDINAL_SUFFIX(rounded)}`)
    }
    const months = num(pick(row, 'observed_month_count'))
    if (months != null && months > 0) chips.push(`seen in ${Math.round(months)} months`)
  }

  return chips
}

/**
 * Server-flagged problems with this row, in plain language.
 *
 * Currently one case: a re-timing that silently became an addition, so the row
 * the user moved is still in the plan next to their copy. Reset removes the
 * duplicate.
 */
export const cashRowWarnings = (row: QuickBooksCashPlanningRow): string[] => {
  const warnings = row.warning_json
  if (!Array.isArray(warnings)) return []
  return warnings
    .map((entry) =>
      entry && typeof entry === 'object' ? str((entry as Record<string, unknown>).message) : null,
    )
    .filter((message): message is string => Boolean(message))
}

/** Short, plain-language sentence for the collapsed modeled card. */
export const describeModeledPlug = (rowLabel: string, accountCount: number): string => {
  const noun = accountCount === 1 ? 'account' : 'accounts'
  return `Estimated from how this business has actually moved cash · ${accountCount} ${noun}`
}

/**
 * The invoice's own expected collectible amount, when the evidence carries it.
 *
 * Prefer this over the row's displayed amount for anything that claims to BE
 * the document: `reconcile_daily_rows_to_monthly_controls` pro-rata nudges every
 * allocatable daily row, including document rows, so the served leg amount is
 * the invoice's share of a reconciled month rather than the invoice itself.
 */
export const documentExpectedAmount = (row: QuickBooksCashPlanningRow): number | null =>
  num(pick(row, 'expected_collectible_amount'))

export const documentSourceDate = (row: QuickBooksCashPlanningRow): string | null =>
  str(pick(row, 'source_date'))
